import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';

// 导入 P5.js 组件
import P5ShinkaiBackground from '../components/home/P5ShinkaiBackground';
import P5WiseSage from '../components/home/P5WiseSage';
import P5Countdown from '../components/home/P5Countdown';

// 导入资源
import floatingSeeds from '../assets/ghibli/floating-seeds.svg';
import leafSvg from '../assets/ghibli/leaf.svg';

// 导入社交媒体图标
import twitterIcon from '../assets/icons/twitter.svg';
import githubIcon from '../assets/icons/github.svg';
import discordIcon from '../assets/icons/discord.svg';
import docsIcon from '../assets/icons/docs.svg';

const LaunchPage = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showCountdown, setShowCountdown] = useState(false);

  // 倒计时完成回调
  const handleCountdownComplete = () => {
    console.log('Countdown completed');
  };

  // 处理按钮点击
  const handleButtonClick = () => {
    setShowCountdown(true);
  };

  // 页面加载动画
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 300);

    return () => clearTimeout(timer);
  }, []);

  // 简化装饰元素
  const decorations = [
    { id: 1, x: 20, y: 30, type: 'leaf' },
    { id: 2, x: 70, y: 40, type: 'seed' },
    { id: 3, x: 50, y: 80, type: 'leaf' }
  ];

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-b from-blue-500/70 via-indigo-600/60 to-indigo-800/70">
      {/* 新海诚风格P5背景 */}
      <P5ShinkaiBackground />

      {/* 移除了原来的阳光光晕效果和模糊白色云朵效果，现在由P5ShinkaiBackground组件处理 */}

      {/* 简化的装饰元素 */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden z-[3]">
        {decorations.map((item) => (
          <motion.div
            key={item.id}
            className="absolute"
            style={{
              left: `${item.x}vw`,
              top: `${item.y}vh`
            }}
            initial={{ opacity: 0 }}
            animate={{
              opacity: [0, 0.6, 0],
              y: [`${item.y}vh`, `${item.y - 10}vh`]
            }}
            transition={{
              duration: 15,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          >
            {item.type === 'leaf' && (
              <img src={leafSvg} alt="Leaf" className="w-8 h-8 opacity-50" />
            )}
            {item.type === 'seed' && (
              <img src={floatingSeeds} alt="Floating Seed" className="w-12 h-12 opacity-40" />
            )}
          </motion.div>
        ))}
      </div>

      {/* 主要内容 */}
      <AnimatePresence>
        {isVisible && (
          <motion.div
            className="relative z-[10] flex flex-col items-center justify-between min-h-screen px-4 py-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            {/* 新海诚风格启动界面 */}
            <div className="flex-grow flex items-center justify-center">
              <motion.div
                className="flex flex-col items-center justify-center space-y-8"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1 }}
              >
                {/* 平面世界智者圆形 - 使用 P5.js 重新实现 */}
                <div className="relative w-64 h-64">
                  <P5WiseSage />
                </div>

                {/* 改进的启动按钮 */}
                <motion.button
                  onClick={handleButtonClick}
                  className="mt-8 px-12 py-4 text-xl font-medium text-white bg-gradient-to-r from-blue-500/80 to-purple-600/80 rounded-full shadow-[0_0_20px_rgba(100,180,255,0.5)] hover:shadow-[0_0_30px_rgba(120,200,255,0.6)] transition-all duration-300 relative overflow-hidden backdrop-blur-sm"
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  whileHover={{ scale: 1.05, boxShadow: '0 0 30px rgba(150,220,255,0.7)' }}
                  whileTap={{ scale: 0.95 }}
                  transition={{ duration: 0.4, delay: 0.2 }}
                >
                  <span className="relative z-10 drop-shadow-md">Genesis Launch</span>

                  {/* 光效扫过动画 */}
                  <motion.span
                    className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent"
                    animate={{
                      x: [-200, 200],
                      opacity: [0, 1, 0]
                    }}
                    transition={{
                      duration: 2.5,
                      repeat: Infinity,
                      repeatDelay: 1,
                      ease: "easeInOut"
                    }}
                  />

                  {/* 边框效果 */}
                  <div className="absolute inset-0 rounded-full border border-white/40 pointer-events-none" />

                  {/* 内部光晕 */}
                  <div className="absolute inset-0 rounded-full bg-gradient-to-r from-blue-400/10 to-purple-500/10" />
                </motion.button>

                {/* 卡片式倒计时 - 点击按钮后显示 */}
                {showCountdown && (
                  <motion.div
                    className="relative w-full flex items-center justify-center mt-8"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, ease: "easeOut" }}
                  >
                    <div className="max-w-2xl w-full px-4">
                      <P5Countdown
                        targetDate="2025-06-15T21:00:00+08:00"
                        onComplete={handleCountdownComplete}
                      />
                    </div>
                  </motion.div>
                )}
              </motion.div>
            </div>

            {/* 页脚部分 */}
            <motion.footer
              className="w-full mt-auto pt-6 z-20"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 1 }}
            >
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex flex-col md:flex-row justify-between items-center py-4 border-t border-white/20">
                  {/* Logo 和版权信息 */}
                  <div className="flex items-center mb-4 md:mb-0">
                    <img
                      src="/logo/logo-medium.png"
                      alt="Flatland Logo"
                      className="h-8 w-auto mr-3"
                    />
                    <p className="text-sm text-white opacity-80">
                      © 2025 Flat World. All rights reserved.
                    </p>
                  </div>

                  {/* 社交媒体图标 */}
                  <div className="flex space-x-6">
                    <a
                      href="https://twitter.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="opacity-80 hover:opacity-100 transition-opacity"
                    >
                      <img src={twitterIcon} alt="Twitter" className="h-5 w-5" />
                    </a>
                    <a
                      href="https://github.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="opacity-80 hover:opacity-100 transition-opacity"
                    >
                      <img src={githubIcon} alt="GitHub" className="h-5 w-5" />
                    </a>
                    <a
                      href="https://discord.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="opacity-80 hover:opacity-100 transition-opacity"
                    >
                      <img src={discordIcon} alt="Discord" className="h-5 w-5" />
                    </a>
                    <Link
                      to="/docs"
                      className="opacity-80 hover:opacity-100 transition-opacity"
                    >
                      <img src={docsIcon} alt="Documentation" className="h-5 w-5" />
                    </Link>
                  </div>
                </div>
              </div>
            </motion.footer>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LaunchPage;
